/**
 * Author: Andrey Morozov
 * Confidential. Proprietary code. Do not distribute without authorization.
 *
 * hooks.ts — hooks (services/operator-web-app).
 */

import { useStore } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { operatorStore } from './operatorStore';
import type { OperatorStore } from './operatorStore';

export function useOperatorStore<T>(selector: (state: OperatorStore) => T): T {
  return useStore(operatorStore, selector);
}

export function useConnectionState() {
  return useOperatorStore((s) => s.connectionState);
}

export function usePresence() {
  return useOperatorStore((s) => s.presence);
}

export function useCall() {
  return useOperatorStore((s) => s.call);
}

export function useWorkspace() {
  return useOperatorStore((s) => s.workspace);
}

export function useCopilot() {
  return useOperatorStore((s) => s.copilot);
}

/** Stable actions; shallow compare avoids re-renders on every store update (can contribute to React #185). */
export function useOperatorActions() {
  return useStore(
    operatorStore,
    useShallow((s) => ({
      connect: s.connect,
      disconnect: s.disconnect,
      send: s.send,
      reset: s.reset,
    }))
  );
}

/** True when operator can receive new calls (available, not paused/offline). */
export function useCanAcceptCall(): boolean {
  return useOperatorStore((s) => s.presence.status === 'available');
}
